// Marco de cada pantalla (documento 07, principio 3): siempre se sabe en qué paso está y cómo va la sincronización.
// Los pasos ya hechos se pueden tocar para mirarlos (sin cambiarlos) y el paso del día siempre lleva de vuelta.
import type { EstadoRuta } from '@reparto/shared/nucleo';
import { cx, type EstadoSync, Icono } from '@reparto/ui';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { db } from '../datos/db';
import { useEstado } from '../datos/ganchos';
import { useSync } from '../datos/sync';
import { useVivo } from '../datos/vivo';
import { BotonChat } from './BotonChat';

export type Paso = 'carga' | 'ruta' | 'entrega' | 'cierre' | null;
const PASOS: Array<{ clave: Exclude<Paso, null>; texto: string; ayuda: string }> = [
  { clave: 'carga', texto: 'Carga', ayuda: 'Ver la carga' },
  { clave: 'ruta', texto: 'Ruta', ayuda: 'Ver la ruta' },
  { clave: 'entrega', texto: 'Entregas', ayuda: 'Ver las paradas' },
  { clave: 'cierre', texto: 'Cierre', ayuda: 'Ver el cierre' },
];

/**
 * A dónde lleva tocar cada paso según cómo va el día, o null si no se puede (un paso que todavía no llegó, o lo
 * que ya no se puede mirar con la ruta cerrada). La carga, con la ruta armada o en curso, se ve en solo lectura.
 */
export function destinoPaso(paso: Exclude<Paso, null>, ruta: EstadoRuta | null | undefined): string | null {
  switch (paso) {
    case 'carga':
      return !ruta || ruta === 'armada' || ruta === 'en_curso' ? '/carga' : null;
    case 'ruta':
      return ruta === 'armada' ? '/ruta/armar' : ruta === 'en_curso' ? '/ruta' : null;
    case 'entrega':
      return ruta === 'en_curso' ? '/ruta' : null;
    case 'cierre':
      return ruta === 'cerrada' ? '/cierre' : null;
  }
}

const SYNC: Record<EstadoSync, { color: string; texto: (n: number) => string; corto: (n: number) => string }> = {
  ok: { color: 'bg-primary-400', texto: () => 'Sincronizado', corto: () => '' },
  pendiente: { color: 'bg-amber-400 animate-pulse', texto: (n) => `${n} por enviar`, corto: (n) => String(n) },
  error: { color: 'bg-rose-500', texto: () => 'Hay errores', corto: () => '!' },
  sin_red: {
    color: 'bg-stone-400',
    texto: (n) => `Sin señal${n ? ` · ${n}` : ''}`,
    corto: (n) => `Sin señal${n ? ` · ${n}` : ''}`,
  },
};

/**
 * Estado de la sincronización. En el celular queda el punto de color y el número de pendientes (o "Sin señal", que
 * el chofer tiene que ver); desde 640 px, el texto entero. Lleva a la sección de sincronización de Perfil.
 */
function Sincronizacion({
  estado,
  pendientes,
  onClick,
}: {
  estado: EstadoSync;
  pendientes: number;
  onClick: () => void;
}) {
  const s = SYNC[estado];
  const largo = s.texto(pendientes);
  const corto = s.corto(pendientes);
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-white/15 px-3 text-xs font-medium"
      aria-label={`Estado de sincronización: ${largo}`}
      title="Ver la sincronización"
    >
      <span className={cx('h-2.5 w-2.5 rounded-full', s.color)} />
      {/* Un solo texto cuando es el mismo en el celular y en pantallas anchas ("Sin señal"). */}
      {corto === largo ? (
        <span>{largo}</span>
      ) : (
        <>
          <span className="hidden sm:inline">{largo}</span>
          {corto ? <span className="tabular-nums sm:hidden">{corto}</span> : null}
        </>
      )}
    </button>
  );
}

/**
 * Ancho del contenido en tablet y notebook (en el celular siempre ocupa toda la pantalla):
 * - `normal`: una columna centrada, cómoda para leer y tocar.
 * - `amplio`: igual hasta 1024 px; desde ahí deja lugar para dos columnas (cada pantalla arma las suyas con `lg:`).
 * - `completo`: de borde a borde, para el mapa a pantalla completa.
 */
export type Ancho = 'normal' | 'amplio' | 'completo';
const ANCHO: Record<Ancho, string> = {
  normal: 'max-w-2xl',
  amplio: 'max-w-2xl lg:max-w-6xl',
  completo: '',
};

export function Marco({
  titulo,
  subtitulo,
  paso = null,
  volver,
  pie,
  ancho = 'normal',
  lateral,
  children,
}: {
  titulo: string;
  subtitulo?: string;
  paso?: Paso;
  volver?: string;
  pie?: ReactNode;
  ancho?: Ancho;
  /**
   * Panel fijo a la derecha (el mapa de la ruta). La pantalla lo pasa solo en pantallas anchas: la lista y su pie
   * quedan en una columna a la izquierda y el panel ocupa el resto del alto.
   */
  lateral?: ReactNode;
  children: ReactNode;
}) {
  const navegar = useNavigate();
  const { pathname } = useLocation();
  const { vista } = useEstado();
  const estadoRuta = vista?.ruta?.estado ?? null;
  const estado = useSync((s) => s.estado);
  // Operaciones guardadas en el celular que todavía no llegaron al servidor.
  const pendientes = useVivo(() => db.cola.where('estado').anyOf('pendiente', 'enviando').count(), [], 0);
  const indicador = estado === 'enviando' || estado === 'pendiente' ? (pendientes ? 'pendiente' : 'ok') : estado;
  const indice = paso ? PASOS.findIndex((p) => p.clave === paso) : -1;
  // La caja centra la cabecera, el cuerpo y el pie con el mismo ancho, así todo queda alineado.
  const caja = cx('mx-auto w-full', ANCHO[lateral ? 'amplio' : ancho]);
  // El pie ocupa todo el ancho de su columna, pero los botones se alinean con el contenido de arriba. Al lado del
  // panel lleva el fondo de la página: es parte de la columna de la lista, no una barra aparte.
  const conPie = (clase: string, fondo = 'bg-white/95 dark:bg-stone-900/95') =>
    pie ? (
      <footer className={cx('seguro-abajo border-t border-stone-200 pt-3 backdrop-blur dark:border-stone-800', fondo)}>
        <div className={cx(clase, 'px-4')}>{pie}</div>
      </footer>
    ) : null;
  return (
    <div className="flex h-full flex-col">
      <header className="seguro-arriba bg-primary-800 text-white">
        <div className={caja}>
          <div className="flex items-center gap-2 px-3 py-2 sm:px-4">
            {volver ? (
              <button
                type="button"
                onClick={() => navegar(volver)}
                className="grid h-10 w-10 place-items-center rounded-full bg-white/10"
                aria-label="Volver"
              >
                <Icono nombre="arrow_back" />
              </button>
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="truncate text-[17px] font-medium leading-tight">{titulo}</div>
              {subtitulo ? <div className="truncate text-xs text-primary-100">{subtitulo}</div> : null}
            </div>
            <Sincronizacion
              estado={indicador}
              pendientes={pendientes}
              onClick={() => navegar('/perfil#sincronizacion')}
            />
            {pathname.startsWith('/chat') ? null : <BotonChat />}
            {pathname !== '/perfil' ? (
              <button
                type="button"
                onClick={() => navegar('/perfil')}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/10"
                aria-label="Perfil"
                title="Perfil"
              >
                <Icono nombre="account_circle" />
              </button>
            ) : null}
          </div>
          {indice >= 0 ? (
            <nav
              className="px-2 pb-1 sm:px-3"
              aria-label={`Paso ${indice + 1} de ${PASOS.length}: ${PASOS[indice]!.texto}`}
            >
              <ol className="flex gap-1">
                {PASOS.map((p, i) => {
                  const destino = destinoPaso(p.clave, estadoRuta);
                  const visto = i === indice;
                  // Se puede tocar si lleva a otra pantalla: un paso hecho (para mirarlo) o el paso del día.
                  const tocable = destino !== null && destino !== pathname;
                  const contenido = (
                    <>
                      <span
                        className={cx('block h-1.5 rounded-full', i <= indice || destino ? 'bg-white' : 'bg-white/25')}
                      />
                      <span
                        className={cx(
                          'mt-0.5 flex items-center text-[10px] uppercase tracking-wide',
                          visto ? 'font-medium text-white' : destino ? 'text-primary-50' : 'text-primary-200/80',
                        )}
                      >
                        {p.texto}
                        {tocable && !visto ? <Icono nombre="chevron_right" tamano={12} /> : null}
                      </span>
                    </>
                  );
                  return (
                    <li key={p.clave} className="min-w-0 flex-1">
                      {tocable ? (
                        <button
                          type="button"
                          onClick={() => navegar(destino)}
                          aria-current={visto ? 'step' : undefined}
                          title={p.ayuda}
                          className="block w-full cursor-pointer rounded-md p-1 text-left hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white"
                        >
                          {contenido}
                        </button>
                      ) : (
                        <span
                          className={cx('block p-1', !destino && !visto && 'cursor-default')}
                          aria-current={visto ? 'step' : undefined}
                          aria-disabled={!destino && !visto ? true : undefined}
                        >
                          {contenido}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          ) : null}
        </div>
      </header>
      {lateral ? (
        <div className={cx(caja, 'flex min-h-0 flex-1')}>
          <div className="flex min-h-0 w-[28rem] shrink-0 flex-col border-r border-stone-200 dark:border-stone-800">
            <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
            {conPie('', 'bg-stone-50/95 dark:bg-stone-950/95')}
          </div>
          <aside className="min-w-0 flex-1 p-4">{lateral}</aside>
        </div>
      ) : (
        <>
          <main className="min-h-0 flex-1 overflow-y-auto">
            {ancho === 'completo' ? children : <div className={caja}>{children}</div>}
          </main>
          {conPie(caja)}
        </>
      )}
    </div>
  );
}

export function Vacio({
  icono,
  titulo,
  texto,
  children,
}: {
  icono: string;
  titulo: string;
  texto?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <span className="grid h-16 w-16 place-items-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/60 dark:text-primary-200">
        <Icono nombre={icono} tamano={32} />
      </span>
      <div className="mt-3 text-lg font-medium">{titulo}</div>
      {texto ? <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">{texto}</p> : null}
      {children ? <div className="mt-4 w-full max-w-xs">{children}</div> : null}
    </div>
  );
}
